import { yupResolver } from '@hookform/resolvers/yup';
import { Form, Space } from 'antd';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { programsApi } from 'src/features/programsV2/api/programsApi';
import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useCreateOrUpdateProgram } from 'src/features/programsV2/hooks/useCreateOrUpdateProgram';
import {
  PublishStepForm,
  publishStepSchema,
} from 'src/features/programsV2/schemas/publishStepSchema';
import { CreateOrUpdateProgramPayload } from 'src/features/programsV2/types/program';
import { formatDate } from 'src/features/programsV2/utils/formatDate';
import {
  showConfirmModal,
  showErrorModal,
  showInfoModal,
  showSuccessModal,
} from 'src/features/programsV2/utils/modalHelpers';
import { getPublishStepPayload } from 'src/features/programsV2/utils/steps/PublishStep/getPublishStepPayload';
import { queryClient } from 'src/queryClient';

import { ProgramStepFooter } from '../../ProgramStepFooter';
import { ProgramPublishContent } from './ProgramPublishContent';

interface PublishStepProps {
  programId?: number;
}

export const PublishStep: FC<PublishStepProps> = ({ programId }) => {
  const navigate = useNavigate();

  const { isPublishedProgram, programApiInitialValues } =
    useProgramStepContext();
  const { createOrUpdateProgram, isLoading: isScheduling } =
    useCreateOrUpdateProgram(programId);
  const [isPublishing, setIsPublishing] = useState(false);

  const apiReleaseDate = programApiInitialValues?.publishStep?.date;

  const form = useForm<PublishStepForm>({
    resolver: yupResolver(publishStepSchema),
    values: programApiInitialValues?.publishStep,
    mode: 'onChange',
  });

  const handleUpdateProgram = async (
    payload: Partial<CreateOrUpdateProgramPayload>
  ) => {
    try {
      await createOrUpdateProgram(payload);
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content:
          'Não foi possível atualizar a data de publicação do programa, tente novamente.',
      });
    }
  };

  const handlePublishProgram = async () => {
    if (!programId) return;

    await programsApi.publishProgram(programId);
    queryClient.invalidateQueries({ queryKey: ['program', programId] });
    queryClient.invalidateQueries({ queryKey: ['program-list'] });
  };

  const onSubmit = async (fields: PublishStepForm) => {
    if (!programId) {
      return;
    }

    const { date, time } = fields;

    if (fields.publish === 'scheduled' && date && time) {
      const timeNow = new Date().getTime();
      const publicationDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        time.getHours(),
        time.getMinutes()
      );

      if (timeNow > publicationDate.getTime()) {
        return showInfoModal({
          title: 'Verifique alguns dados',
          content:
            'Sua data de publicação não pode ser anterior a sua data atual.',
        });
      }

      const releaseDate = getPublishStepPayload(publicationDate);

      const payload: Partial<CreateOrUpdateProgramPayload> = {
        releaseDate,
      };

      return showConfirmModal({
        title: apiReleaseDate
          ? 'Confirmar Reagendamento'
          : 'Confirmar Agendamento',
        content: apiReleaseDate
          ? `Este programa já possui uma data de publicação agendada para o dia ${formatDate(
              apiReleaseDate.toISOString(),
              true
            )}. Você deseja realmente reagendar? Esta ação não pode ser desfeita.`
          : `Gostaria de agendar a publicação do programa para ${formatDate(
              publicationDate.toISOString(),
              true
            )}? Esta ação não pode ser desfeita.`,
        onOk: async () => {
          await handleUpdateProgram(payload);
          showSuccessModal({
            title: 'Sucesso',
            content: 'O programa foi reagendado com sucesso!',
          });
        },
      });
    }

    try {
      setIsPublishing(true);
      showConfirmModal({
        title: 'Confirmar Publicação',
        content: apiReleaseDate
          ? `Este programa tem uma data de publicação agendada para o dia ${formatDate(
              apiReleaseDate.toISOString(),
              true
            )}. Você deseja realmente publicar agora? Esta ação não pode ser desfeita.`
          : 'Tem certeza de que deseja publicar as alterações? Esta ação não pode ser desfeita.',
        onOk: async () => {
          await handlePublishProgram();
          showSuccessModal({
            title: 'Sucesso',
            content: 'O programa foi publicado com sucesso!',
          });
        },
      });
    } catch (error) {
      showErrorModal({
        title: 'Tivemos um problema...',
        content:
          'Não foi possível publicar o programa, por favor tente novamente.',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const publishButtonText =
    !form.watch()?.publish || form.watch().publish === 'now'
      ? `Publicar`
      : !!apiReleaseDate
        ? 'Reagendar'
        : 'Agendar';

  const localDate = form.watch().date;
  const localTime = form.watch().time;

  const isSamePublishDate =
    !!apiReleaseDate &&
    localDate &&
    localTime &&
    new Date(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localTime.getHours(),
      localTime.getMinutes()
    ).toISOString() === new Date(apiReleaseDate).toISOString();

  const disablePublishButton =
    !form.watch()?.publish ||
    (form.watch()?.publish === 'scheduled' &&
      (!localTime || !localDate || !!isSamePublishDate));

  return (
    <FormProvider {...form}>
      <Form layout='vertical' onFinish={form.handleSubmit(onSubmit)}>
        <Space direction='vertical' size={24} style={{ width: '100%' }}>
          <ProgramPublishContent isPublished={isPublishedProgram} />
        </Space>
        <ProgramStepFooter
          programId={programId}
          isNextButtonDisabled={disablePublishButton}
          nextButtonText={publishButtonText}
          onCancelConfirmation={() => navigate('/programs/v2')}
          isLoadingNextButton={isPublishing || isScheduling}
          removeNextButton={isPublishedProgram}
          style={{
            left: 0,
            right: 0,
            bottom: 0,
            position: 'fixed',
          }}
        />
      </Form>
    </FormProvider>
  );
};
